import { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";

import { useTran } from "@/hooks";
import { AppDialog, MultiSelect, SelectField } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  BU_CATEGORY,
  LOCATION_OPTIONS,
  ORGANIZATION_REGION_OPTIONS,
} from "@/lib/constants";
import { useOrganizationStore } from "../hooks";
import { validateOrganizationUnitForm } from "../schemas";

const DEFAULT_FORM = {
  unitLevel: "level_1",
  unitType: "",
  businessTypes: [],
  name: "",
  code: "",
  parentId: "",
  region: "",
  country: "",
  description: "",
  isActive: true,
};

const LEVEL_REGEX = /^level_(\d+)$/;

const getLevelNumber = (unitLevel) => {
  if (!unitLevel) return null;
  const match = LEVEL_REGEX.exec(unitLevel);
  if (!match) return null;
  const level = Number(match[1]);
  return Number.isFinite(level) && level > 0 ? level : null;
};

const buildLevelOptions = (maxLevel) => {
  const safeMax = Math.min(3, Math.max(1, (maxLevel || 0) + 1));
  return Array.from({ length: safeMax }, (_, index) => {
    const level = index + 1;
    return { key: `level_${level}`, label: `Level ${level}`, level };
  });
};

const getCountriesByRegion = (regionKey, allOptions) => {
  if (!regionKey) return [];
  if (regionKey === "global") return allOptions;
  const hasRegions = allOptions.some((option) => Array.isArray(option.regions));
  if (!hasRegions) return allOptions;
  return allOptions.filter(
    (c) => Array.isArray(c.regions) && c.regions.includes(regionKey),
  );
};

const OrganizationUnitDialog = observer(
  ({ open, mode = "create", initialValues = {}, onSubmit, onOpenChange }) => {
    const { t } = useTran();
    const { items, maxLevel, countryOptions, saving } = useOrganizationStore();
    const [form, setForm] = useState(DEFAULT_FORM);
    const [errors, setErrors] = useState({});

    useEffect(() => {
      if (!open) return;
      setErrors({});
      setForm({
        ...DEFAULT_FORM,
        ...initialValues,
        code: initialValues?.code || "",
        unitType: initialValues?.unitType || "",
        businessTypes: Array.isArray(initialValues?.businessTypes)
          ? initialValues.businessTypes
          : [],
        parentId: initialValues?.parentId || "",
        region: initialValues?.region || "",
        country: initialValues?.country || "",
        isActive:
          typeof initialValues?.isActive === "boolean"
            ? initialValues.isActive
            : true,
      });
    }, [open, initialValues]);

    const selectedLevel = getLevelNumber(form.unitLevel);

    const parentChoices = useMemo(() => {
      if (!selectedLevel || selectedLevel <= 1) return [];
      const parentLevel = selectedLevel - 1;
      return (items || [])
        .filter((unit) => getLevelNumber(unit.unitLevel) === parentLevel)
        .map((unit) => ({ id: unit.id, name: unit.name }))
        .sort((a, b) => a.name.localeCompare(b.name));
    }, [items, selectedLevel]);

    const levelOptions = useMemo(
      () =>
        buildLevelOptions(maxLevel).map((option) => ({
          ...option,
          label: t(option.label),
        })),
      [maxLevel, t],
    );
    const regionOptions = useMemo(
      () =>
        ORGANIZATION_REGION_OPTIONS.map((option) => ({
          ...option,
          label: t(option.label),
        })),
      [t],
    );
    const businessTypeOptions = useMemo(
      () =>
        BU_CATEGORY.map((option) => ({
          value: option.key ?? option.value,
          label: t(option.value ?? option.label ?? option.key),
        })),
      [t],
    );
    const countrySource = useMemo(() => {
      const source = countryOptions.length ? countryOptions : LOCATION_OPTIONS;
      return (source || [])
        .map((option) => {
          const key =
            option.key ??
            option.value ??
            option.code ??
            option.id ??
            option.name;
          if (!key) return null;
          return {
            key,
            label:
              option.label ??
              option.value ??
              option.name ??
              option.key ??
              String(key),
            regions: option.regions,
          };
        })
        .filter(Boolean);
    }, [countryOptions]);
    const regionCountryOptions = useMemo(() => {
      const list = getCountriesByRegion(form.region, countrySource);
      const seen = new Set();
      return list.filter((item) => {
        if (seen.has(item.key)) return false;
        seen.add(item.key);
        return true;
      });
    }, [form.region, countrySource]);

    const handleInput = (field, value) => {
      setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleSelectChange = (field) => (input) => {
      const value = input?.target ? input.target.value : input;
      handleInput(field, value);
    };

    const handleUnitLevelChange = (input) => {
      const value = input?.target ? input.target.value : input;
      setForm((prev) => {
        const next = { ...prev, unitLevel: value };
        const nextLevel = getLevelNumber(value);
        if (!nextLevel || nextLevel <= 1) {
          next.parentId = "";
        } else {
          next.parentId = "";
        }
        return next;
      });
    };

    const submit = async (event) => {
      event.preventDefault();
      const nextErrors = validateOrganizationUnitForm(form, {
        parentChoicesLength: parentChoices.length,
        t,
      });
      setErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) return;

      const level = getLevelNumber(form.unitLevel);
      const normalizedParentId =
        level && level > 1
          ? form.parentId && form.parentId.length > 0
            ? form.parentId
            : null
          : null;

      const payload = {
        name: form.name,
        code: form.code,
        unitLevel: form.unitLevel,
        unitType: form.unitType || null,
        businessTypes: Array.isArray(form.businessTypes)
          ? form.businessTypes
          : [],
        parentId: normalizedParentId,
        region: form.region || null,
        country: form.country || null,
        description: form.description || null,
        isActive: Boolean(form.isActive),
      };

      await onSubmit?.(payload);
    };

    return (
      <AppDialog
        open={open}
        onOpenChange={onOpenChange}
        title={
          mode === "create"
            ? t("Add organization unit")
            : t("Edit organization unit")
        }
        description={t("Define the unit details, hierarchy, and location.")}
        size="lg"
        footer={
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange?.(false)}
              disabled={saving}
            >
              {t("Cancel")}
            </Button>
            <Button
              type="submit"
              form="organization-unit-form"
              disabled={saving}
            >
              {saving
                ? t("Saving...")
                : mode === "create"
                  ? t("Create")
                  : t("Update")}
            </Button>
          </>
        }
      >
        <form
          id="organization-unit-form"
          onSubmit={submit}
          className="space-y-5"
        >
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="unit-name">
                {t("Unit Name")}
                <span className="text-destructive">*</span>
              </Label>
              <Input
                id="unit-name"
                value={form.name}
                onChange={(event) => handleInput("name", event.target.value)}
                placeholder={t("Enter unit name")}
                className={errors.name ? "border-destructive" : ""}
              />
              {errors.name ? (
                <p className="text-xs text-destructive">{errors.name}</p>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="org-code">
                {t("Unit Code")}
                <span className="text-destructive">*</span>
              </Label>
              <Input
                id="org-code"
                value={form.code}
                onChange={(event) =>
                  handleInput("code", event.target.value.replace(/\s+/g, ""))
                }
                placeholder={t("Enter unit code")}
                className={errors.code ? "border-destructive" : ""}
              />
              {errors.code ? (
                <p className="text-xs text-destructive">{errors.code}</p>
              ) : null}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <SelectField
              id="unitLevel"
              label={t("Unit Level")}
              value={form.unitLevel || undefined}
              onChange={handleUnitLevelChange}
              options={levelOptions}
              placeholder={t("Select unit level")}
              error={errors.unitLevel}
            />
            <SelectField
              key={`parent-${form.unitLevel}`}
              id="parentId"
              label={t("Parent Unit")}
              value={form.parentId || undefined}
              onChange={handleSelectChange("parentId")}
              options={parentChoices.map((option) => ({
                key: option.id,
                label: option.name,
              }))}
              placeholder={t("Select parent unit")}
              error={errors.parentId}
              disabled={!selectedLevel || selectedLevel <= 1}
              emptyLabel={t("No parent units available")}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="business-units">{t("Business Type")}</Label>
              <MultiSelect
                options={businessTypeOptions}
                value={form.businessTypes || []}
                onChange={(value) => handleInput("businessTypes", value)}
                placeholder={t("Select business type")}
                className={errors.businessTypes ? "border-destructive" : ""}
              />
              {errors.businessTypes ? (
                <p className="text-xs text-destructive">
                  {errors.businessTypes}
                </p>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="unit-type">{t("Unit Type")}</Label>
              <Input
                id="unit-type"
                value={form.unitType}
                onChange={(event) =>
                  handleInput("unitType", event.target.value)
                }
                placeholder={t("E.g., Subsidiary")}
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <SelectField
              id="region"
              label={t("Region")}
              required
              value={form.region || undefined}
              onChange={handleSelectChange("region")}
              options={regionOptions}
              placeholder={t("Select region")}
              error={errors.region}
            />
            <SelectField
              id="country"
              label={t("Country")}
              required
              value={form.country || undefined}
              onChange={handleSelectChange("country")}
              options={regionCountryOptions}
              placeholder={t("Select country")}
              error={errors.country}
              emptyLabel={t("No countries available")}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="flex items-center justify-between rounded-radius border border-border p-3">
              <div>
                <p className="text-sm font-medium text-foreground">
                  {t("Active")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t("Toggle if this unit is active.")}
                </p>
              </div>
              <Switch
                checked={Boolean(form.isActive)}
                onCheckedChange={(checked) => handleInput("isActive", checked)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="unit-description">{t("Description")}</Label>
              <Textarea
                id="unit-description"
                value={form.description}
                onChange={(event) =>
                  handleInput("description", event.target.value)
                }
                placeholder={t("Add notes or industry classification")}
              />
            </div>
          </div>
        </form>
      </AppDialog>
    );
  },
);

export default OrganizationUnitDialog;
